import React, { Component, ErrorInfo, ReactNode, useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, Typography, Button } from '@mui/material';
import './App.css';
import AppRoutes from './routes/AppRoutes';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider, useThemeContext } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { getTheme } from './theme/theme';
import { errorTracker, trackPerformance } from './services/errorTrackingService';
// import DatabaseChecker from './debug/DatabaseChecker';

// Safari/iOS detection helper
const isSafari = (): boolean => {
  if (typeof window === 'undefined') return false;
  const userAgent = window.navigator.userAgent;
  return /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
};

const isIOS = (): boolean => {
  if (typeof window === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(window.navigator.userAgent);
};

// Safari debugging helper
const logSafariDebugInfo = () => {
  if (typeof window === 'undefined') return;
  
  console.log('[Safari Debug] User Agent:', window.navigator.userAgent);
  console.log('[Safari Debug] Is Safari:', isSafari());
  console.log('[Safari Debug] Is iOS:', isIOS());
  console.log('[Safari Debug] Screen size:', window.screen.width, 'x', window.screen.height);
  console.log('[Safari Debug] Window size:', window.innerWidth, 'x', window.innerHeight);
  console.log('[Safari Debug] Document ready state:', document.readyState);
  console.log('[Safari Debug] Timestamp:', new Date().toISOString());
};

// Error Boundary Component
interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  isSafari: boolean;
  isIOS: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    isSafari: isSafari(),
    isIOS: isIOS()
  };

  public static getDerivedStateFromError(error: Error): State {
    // Log Safari-specific debug info when error occurs
    logSafariDebugInfo();
    console.error('[Safari] Error caught by boundary:', error.message);
    
    // Update state so the next render will show the fallback UI
    return { 
      hasError: true,
      isSafari: isSafari(),
      isIOS: isIOS()
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[Safari] ErrorBoundary caught an error:', error, errorInfo);
    console.error('[Safari] Error stack:', error.stack);
    console.error('[Safari] Component stack:', errorInfo.componentStack);

    // Report to error tracking service
    errorTracker.captureComponentError(error, errorInfo.componentStack || '');

    // Additional Safari debugging
    logSafariDebugInfo();

    this.setState({
      error,
      errorInfo
    });
  }

  public render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            padding: 3,
            backgroundColor: '#f5f5f5',
            textAlign: 'center'
          }}
        >
          <Typography variant="h4" sx={{ mb: 2, color: '#d32f2f' }}>
            Oops! Something went wrong
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, maxWidth: 600 }}>
            The app encountered an unexpected error. Please try refreshing the page, or if the problem persists, contact support.
          </Typography>
          {(this.state.isSafari || this.state.isIOS) && (
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 2, 
                padding: 2, 
                backgroundColor: '#e3f2fd', 
                borderRadius: 1,
                fontSize: '0.75rem',
                color: '#1976d2'
              }}
            >
              Safari/iOS detected - Debug info logged to console
            </Typography>
          )}
          {this.state.error && (
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 3, 
                padding: 2, 
                backgroundColor: '#ffebee', 
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                color: '#d32f2f'
              }}
            >
              Error: {this.state.error.message}
              {(this.state.isSafari || this.state.isIOS) && (
                <div style={{ marginTop: '8px', fontSize: '0.75rem' }}>
                  Browser: {this.state.isSafari ? 'Safari' : 'Other'} | 
                  Platform: {this.state.isIOS ? 'iOS' : 'Other'}
                </div>
              )}
            </Typography>
          )}
          <Button
            variant="contained"
            onClick={() => {
              this.setState({ hasError: false, error: undefined, errorInfo: undefined });
              window.location.reload();
            }}
            sx={{
              background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
              color: 'white',
              padding: '12px 24px'
            }}
          >
            Refresh Page
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

// Theme-aware app content
const ThemedApp: React.FC = () => {
  const { darkMode } = useThemeContext();
  const currentTheme = getTheme(darkMode ? 'dark' : 'light');

  return (
    <MuiThemeProvider theme={currentTheme}>
      <CssBaseline />
      <Router>
        <LanguageProvider>
          <AuthProvider>
            <AppRoutes debugComponent={null} />
          </AuthProvider>
        </LanguageProvider>
      </Router>
    </MuiThemeProvider>
  );
};

function App() {
  // Early Safari debugging and performance monitoring
  React.useEffect(() => {
    if (isSafari() || isIOS()) {
      logSafariDebugInfo();
    }

    // Track app initialization performance
    const startTime = performance.now();

    // Track when app becomes interactive
    const trackInteractive = () => {
      const loadTime = performance.now() - startTime;
      trackPerformance('app_load_time', loadTime);

      // Track navigation timing if available
      if (window.performance && window.performance.timing) {
        const timing = window.performance.timing;
        const pageLoadTime = timing.loadEventEnd - timing.navigationStart;
        if (pageLoadTime > 0) {
          trackPerformance('page_load_time', pageLoadTime);
        }

        const domContentLoaded = timing.domContentLoadedEventEnd - timing.navigationStart;
        if (domContentLoaded > 0) {
          trackPerformance('dom_content_loaded', domContentLoaded);
        }
      }
    };

    // Wait for page to be fully loaded
    if (document.readyState === 'complete') {
      trackInteractive();
    } else {
      window.addEventListener('load', trackInteractive);
      return () => window.removeEventListener('load', trackInteractive);
    }
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <ThemedApp />
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App; 